import { yupResolver } from '@hookform/resolvers/yup'
import { useCallback, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { redeemTaxTypes } from './AddInviteModal'
import Loader from '../../../common/components/Loader'
import { Languages, TicketCategory } from '../../../common/models'
import { InviteTicketsList } from '../../../common/models/ticket'
import FormGroup from '../../../core/components/FormGroup'
import { formatDateTime } from '../../../core/constants/times'
import { getSettingsLanguages } from '../../../core/services/festival-settings'
import {
  IInvite,
  cancelInvite,
  downloadInvitePdf,
  getInvite,
  updateInvite,
} from '../../../core/services/invites'
import {
  getAllCategories,
  getInviteTicketsByCategoryId,
} from '../../../core/services/tickets'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'
import { PageTitle } from '@/components'
import { Button, Checkbox, Input, Modal } from '@/components/ui'
import SelectField from '@/components/select-field'
import { DateTimePicker } from '@/components/ui/date-time-picker'

interface IEditInviteValues {
  label: string
  locale: string
  category: string
  ticketId: string
  price: number
  freeCheckin: boolean
  freeRedeem?: boolean
  generateType?: string
  email?: string
  dubaiPriceTypeCode?: string
  intownTicketId?: string
  redeemTaxType?: string
  taxPrice?: number | null
  dubaiIntegration?: boolean
  used?: boolean
  usedDate?: string
}

export interface CreateInviteProps {
  visible: boolean
  setVisible: (show: boolean) => void
  invite: IInvite | undefined
  dubaiIntegration: boolean
  currency: string
  getData: () => Promise<void>
}

export const EditInvite = (props: CreateInviteProps): JSX.Element => {
  const { visible, setVisible, invite, dubaiIntegration, currency } = props

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [showDeleteConfirmationModal, setDeleteConfirmationModal] =
    useState<boolean>(false)

  const [showCancelInviteModal, setShowCancelInviteModal] =
    useState<boolean>(false)

  const [inviteData, setInviteData] = useState<IInvite>()

  const [ticketCategoriesData, setTicketCategoriesData] = useState<
    TicketCategory[]
  >([])
  const [loading, setLoading] = useState<boolean>(false)
  const [submitLoading, setSubmitLoading] = useState<boolean>(false)
  const [allLanguages, setAllLanguages] = useState<Languages[]>([])
  const [tickets, setTickets] = useState<InviteTicketsList[]>()

  const getData = useCallback(async () => {
    if (!invite) return
    try {
      setLoading(true)
      const { data: catData, status: catStatus } = await getAllCategories({
        size: 1000,
      })
      if (catStatus === 200) {
        setTicketCategoriesData(catData.content)
      }
      const allLanguagesResponse = await getSettingsLanguages()
      setAllLanguages(allLanguagesResponse.data)
      const { data, status } = await getInvite(invite.id)
      if (status === 200) {
        setInviteData(data)
        setValue('locale', data.locale)
        setValue('label', data.label, {
          shouldValidate: true,
        })
        if (data.email) {
          setValue('email', data.email, { shouldValidate: true })
        }
        if (data.ticket.category) {
          setValue('category', data.ticket.category.id, {
            shouldValidate: true,
          })
        }
        if (data.ticket.ticket) {
          setValue('ticketId', data.ticket.ticket.id, {
            shouldValidate: true,
          })
        }
        setValue('price', data.price)
        setValue('redeemTaxType', data.taxType, {
          shouldValidate: true,
        })
        if (data.freeCheckin) {
          setValue('freeCheckin', data.freeCheckin)
        }
        if (data.freeRedeem) {
          setValue('freeRedeem', data.freeRedeem)
        }
        if (data.used) {
          setValue('used', data.used)
        }
        if (data.usedDateTimestamp > 0) {
          setValue('usedDate', formatDateTime(data.usedDateTimestamp))
        }
        if (data.tax) {
          setValue('taxPrice', data.tax)
        }
      }
    } catch (err) {
      console.log(err)
    } finally {
      setLoading(false)
    }
  }, [invite])

  const handleCancelInvite = useCallback(async () => {
    try {
      setLoading(true)
      if (invite) {
        const resp = await cancelInvite(invite.id)
        if (resp.status === 200) {
          setVisible(false)
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Cancel Successful!')
          props.getData()
        }
      } else {
        throw new Error('No Invitation')
      }
    } catch (error) {
      if (error instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(error.message)
        setErrorStatus(
          (error as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(error)
      }
    } finally {
      setLoading(false)
      setShowCancelInviteModal(false)
    }
  }, [getData])

  const downloadPdf = async () => {
    if (!invite?.id) return
    try {
      setLoading(true)
      const response = await downloadInvitePdf(invite?.id)

      const href = URL.createObjectURL(response.data)

      // create "a" HTML element with href to file & click
      const link = document.createElement('a')
      link.href = href
      link.setAttribute('download', `${invite?.id}.pdf`) //or any other extension
      document.body.appendChild(link)
      link.click()

      // clean up "a" element & remove ObjectURL
      document.body.removeChild(link)
      URL.revokeObjectURL(href)
    } catch (err) {
      console.log(err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (invite?.id) {
      getData()
    }

    return () => {
      reset()
    }
  }, [invite, visible])

  useEffect(() => {
    setValue('dubaiIntegration', dubaiIntegration, {
      shouldValidate: true,
    })
  }, [dubaiIntegration])

  const handleClose = () => {
    setVisible(false)
  }

  const schema = yup.object().shape({
    locale: yup.string().required('Label is required'),
    label: yup.string().required('Label is required'),
    category: yup.string().required('Category is required'),
    ticketId: yup.string().required('Ticket is required'),
    price: yup
      .number()
      .transform((v) => (isNaN(v) ? 0 : v))
      .required(),
    freeCheckin: yup.boolean().required(),
    freeRedeem: yup.boolean().when('used', {
      is: (val: boolean) => val,
      then: (schema) => schema.optional(),
      otherwise: (schema) => schema.required(),
    }),
    generateType: yup.string().when('dubaiIntegration', {
      is: (val: boolean) => val,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.optional(),
    }),
    email: yup.string().email(),
    dubaiPriceTypeCode: yup.string().optional(),
    intownTicketId: yup.string().optional(),
    redeemTaxType: yup.string().required(),
    taxPrice: yup.number().when('redeemTaxType', {
      is: (val: string) => val === 'PAID',
      then: (schema) => schema.required(),
      otherwise: (schema) =>
        schema.optional().transform((v) => (isNaN(v) ? 0 : v)),
    }),
    dubaiIntegration: yup.boolean().optional(),
    used: yup.boolean().optional(),
    usedDate: yup.string().optional(),
  })

  const {
    register,
    setValue,
    control,
    handleSubmit,
    formState: { errors, dirtyFields },
    reset,
    watch,
  } = useForm<IEditInviteValues>({
    mode: 'onChange',
    resolver: yupResolver<IEditInviteValues>(schema),
  })

  const category = watch('category')
  const redeemTaxType = watch('redeemTaxType')

  const getCategoryTickets = useCallback(async () => {
    try {
      const { data } = await getInviteTicketsByCategoryId(category, {
        size: 1000,
      })
      setTickets(data)
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
  }, [category])

  useEffect(() => {
    if (category) {
      getCategoryTickets()
    }
  }, [category])

  const deleteInvite = async () => {
    try {
      if (invite) {
        handleClose()
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
  }

  const submit = async (values: IEditInviteValues) => {
    if (!inviteData) return
    try {
      setSubmitLoading(true)
      const { data, status } = await updateInvite(inviteData.id, {
        freeCheckin: values.freeCheckin,
        freeRedeem: values.freeRedeem,
        used: inviteData.used ? inviteData.used : values.used,
      })
      if (status === 200) {
        setInviteData(data)
      }
    } catch (err) {
      console.log(err)
    } finally {
      setSubmitLoading(false)
    }
  }

  return (
    <>
      <Modal
        open={visible}
        onOpenChange={(open) => {
          if (!open) handleClose()
        }}
        size="xl"
        title={
          <div className="border-b border-monochrome-120 pb-4">
            <PageTitle title="View Invite / Ticket" />
          </div>
        }
        footer={
          <div className="flex w-full items-center justify-between gap-2">
            <Button
              onClick={() => setShowCancelInviteModal(true)}
              variant="secondary"
              className="bg-accent-3 hover:bg-accent-3/90"
              type="button"
            >
              Cancel Invite
            </Button>
            <div className="flex gap-2">
              <Button onClick={handleClose} type="button" variant="secondary">
                Cancel
              </Button>
              <Button
                form="invite-form"
                type="submit"
                variant="primary"
                disabled={
                  submitLoading || Object.keys(dirtyFields).length === 0
                }
                loading={submitLoading}
              >
                Save
              </Button>
            </div>
          </div>
        }
      >
        {loading && <Loader />}
        <form
          id="invite-form"
          onSubmit={handleSubmit(submit)}
          className="space-y-7"
        >
          <FormGroup
            label="Invite Language"
            tooltip="Invite will be generated in this Language"
            error={errors?.locale?.message}
          >
            <SelectField
              outline
              {...register(`locale`)}
              value={watch('locale') || ''}
              onValueChange={(value) => setValue('locale', value)}
              options={allLanguages.map((lang) => ({
                label: lang.name,
                value: lang.locale,
              }))}
              disabled
              error={errors?.locale?.message}
              triggerClassName="h-[44px] rounded-2xl"
            />
          </FormGroup>
          <FormGroup
            label="Label Name (* Only for internal use)"
            error={errors.label?.message}
          >
            <Input
              {...register('label')}
              disabled
              type="text"
              isInvalid={!!errors.label?.message}
            />
          </FormGroup>
          <div className="flex flex-row items-start gap-3">
            <FormGroup
              label="Applied for Categories Tickets"
              tooltip="Select Applied for Categories Tickets"
              error={errors.category?.message}
            >
              <Controller
                name="category"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <SelectField
                    outline
                    value={value || ''}
                    onValueChange={onChange}
                    options={ticketCategoriesData.map((ticket) => ({
                      label: ticket.name,
                      value: ticket.id,
                    }))}
                    disabled
                    placeholder="Select Applied for Categories Tickets"
                    error={errors.category?.message}
                    triggerClassName="h-[44px] rounded-2xl"
                  />
                )}
              />
            </FormGroup>
            <FormGroup
              label="Select Ticket"
              tooltip="Select Ticket"
              error={errors.ticketId?.message}
            >
              <Controller
                name="ticketId"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <SelectField
                    outline
                    value={value || ''}
                    onValueChange={onChange}
                    options={
                      tickets?.map((ticket) => ({
                        label: `${ticket.category.name} - ${ticket.ticket.name}`,
                        value: ticket.ticket.id,
                      })) || []
                    }
                    disabled
                    placeholder="Select Ticket"
                    error={errors.ticketId?.message}
                    triggerClassName="h-[44px] rounded-2xl"
                  />
                )}
              />
            </FormGroup>
          </div>
          <div className="flex justify-between gap-2">
            <FormGroup
              label="Ticket Price"
              wrapperClassName="w-full"
              error={errors.price?.message}
            >
              <Input
                {...register('price')}
                type="number"
                min={0}
                disabled
                placeholder={`0 ${currency} (invited)`}
                isInvalid={!!errors.price?.message}
              />
            </FormGroup>
            <div className="w-full">
              <FormGroup
                label="Redeem Tax (Paid or Free)"
                error={errors.redeemTaxType?.message}
              >
                <SelectField
                  outline
                  {...register('redeemTaxType')}
                  value={watch('redeemTaxType') || ''}
                  onValueChange={(value) => setValue('redeemTaxType', value)}
                  options={redeemTaxTypes.map((type) => ({
                    label: type.label,
                    value: type.value,
                  }))}
                  disabled
                  error={errors.redeemTaxType?.message}
                  triggerClassName="h-[44px] rounded-2xl"
                />
              </FormGroup>
              {redeemTaxType === 'PAID' && (
                <FormGroup
                  wrapperClassName="mt-2"
                  label="Tax Price"
                  error={errors.taxPrice?.message}
                >
                  <Input
                    {...register('taxPrice')}
                    type="number"
                    min={0}
                    placeholder="Tax Price"
                    isInvalid={!!errors.taxPrice?.message}
                  />
                </FormGroup>
              )}
            </div>
          </div>
          <div className="space-y-4">
            <FormGroup
              label="Free Checkin"
              direction="row"
              labelClassName="whitespace-nowrap"
              inputWrapperClassName="w-auto"
              wrapperClassName="w-auto flex-row-reverse justify-end"
              labelProps={{ htmlFor: 'freeCheckin' }}
            >
              <Controller
                name="freeCheckin"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    id="freeCheckin"
                    checked={Boolean(field.value)}
                    onCheckedChange={(checked) =>
                      field.onChange(Boolean(checked))
                    }
                  />
                )}
              />
            </FormGroup>
            {!inviteData?.used && (
              <FormGroup
                label="Free Redeem"
                direction="row"
                labelClassName="whitespace-nowrap"
                inputWrapperClassName="w-auto"
                wrapperClassName="w-auto flex-row-reverse justify-end"
                labelProps={{ htmlFor: 'freeRedeem' }}
              >
                <Controller
                  name="freeRedeem"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      id="freeRedeem"
                      checked={Boolean(field.value)}
                      onCheckedChange={(checked) =>
                        field.onChange(Boolean(checked))
                      }
                    />
                  )}
                />
              </FormGroup>
            )}
          </div>
          <FormGroup
            direction="column"
            label="Select emails to send invitations"
          >
            <Input {...register('email')} type="text" disabled />
          </FormGroup>
          <Button
            onClick={downloadPdf}
            type="button"
            variant="secondary"
            className="w-full mt-2"
          >
            Download PDF
          </Button>
          <FormGroup
            label="Used"
            direction="row"
            labelClassName="whitespace-nowrap"
            inputWrapperClassName="w-auto"
            wrapperClassName="w-auto flex-row-reverse justify-end"
            labelProps={{ htmlFor: 'used' }}
          >
            <Controller
              name="used"
              control={control}
              render={({ field }) => (
                <Checkbox
                  id="used"
                  checked={Boolean(field.value)}
                  onCheckedChange={(checked) =>
                    field.onChange(Boolean(checked))
                  }
                  disabled={inviteData?.used}
                />
              )}
            />
          </FormGroup>
          <FormGroup label="Used Date" error={errors?.usedDate?.message}>
            <DateTimePicker
              value={
                watch('usedDate') ? new Date(watch('usedDate') as string) : null
              }
              onChange={(date) => {
                setValue(
                  'usedDate',
                  date ? formatDateTime(date.toISOString()) : undefined,
                )
              }}
              placeholder="Enter Publish Date"
              hasError={!!errors?.usedDate?.message}
              disabled
              className="h-[44px] rounded-2xl"
            />
          </FormGroup>
        </form>
      </Modal>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteConfirmationModal}
        setDeleteConfirmationModal={setDeleteConfirmationModal}
        deleteTitle={'Invite'}
        deleteFunc={deleteInvite}
      />
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showCancelInviteModal}
        setDeleteConfirmationModal={setShowCancelInviteModal}
        cancelText
        deleteFunc={() => {
          handleCancelInvite()
        }}
        deleteTitle={'Invite'}
      />
    </>
  )
}
